import {
  Component,
  NgZone,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CommonService } from '../../../../../../shared/services/common.service';
import { CustomerSharedService } from '../../../../customer-shared.service';
import { DeviceService } from '../../device.service';

import * as XLSX from 'xlsx';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-transport-sensor-management',
  templateUrl: './transport-sensor-management.component.html',
  styleUrls: ['./sensor.component.css'],
})
export class TransportSensorManagementComponent implements OnInit {
  // Data lists
  sensorList$: any[] = [];
  filteredSensorList$: any[] = [];
  modelNumbers: any[] = [];
  deviceEUIs: any[] = [];
  filteredDeviceEUIs: any[] = [];
  locationOptions: any[] = [];

  // File upload
  fileList: File[] = [];
  excelErr: string = '';
  excelErrLink: string = '';
  TRANSPORT_EXPECTED_HEADERS = ['deviceeui', 'parent_location'];
  isSuccessMessage: boolean = false;

  // Modal states
  isExcelModalVisible = false;
  isSensorModalVisible = false;
  isConfirmLoading = false;
  isEditMode: boolean = false;
  editData: any = null;
  originalTransportData: any = null;

  quickFilter: 'all' | 'active' | 'inActive' = 'all';
  counts = { total_count: 0, active_count: 0, inactive_count: 0 };
  searchText!: string;

  transportForm!: FormGroup;
  customer_id: string;
  apiCallLoaders = { isSensorsLoading: false };

  // Table columns configuration
  listOfColumns: any[] = [
    { name: 'LAST SEEN LOCATION', key: 'last_seen_location' },
    { name: 'SERIAL NO', key: 'serial_no' },
    { name: 'MODEL NO', key: 'model_no' },
    { name: 'DEVEUI', key: 'deviceeui' },
    { name: 'CAPABILITIES', key: 'capabilities' },
    { name: 'CURRENT STATUS', key: 'current_status' },
    { name: 'LAST SEEN', key: 'last_seen' },
    { name: 'LOCATION', key: 'display_name' },
  ];

  constructor(
    public customerSharedService: CustomerSharedService,
    public route: ActivatedRoute,
    private fb: FormBuilder,
    public deviceService: DeviceService,
    public commonService: CommonService,
    public customerSharedSvc: CustomerSharedService,
    private ngZone: NgZone
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit() {
    this.initializeForm();
    this.loadInitialData();
  }

  initializeForm() {
    this.transportForm = this.fb.group({
      serial_no: [null, Validators.required],
      model_no: [null, Validators.required],
      deviceeui: [null, Validators.required],
      capabilities: [null],
      parent_location: [null, Validators.required],
    });
  }

  loadInitialData() {
    this.getSensorsbyCustomerId();
    this.getWhitelistData();
    this.getLocationByCustomerId();
    this.filteredSensorList$ = [];
  }

  getSensorsbyCustomerId() {
    this.apiCallLoaders.isSensorsLoading = true;
    this.counts = { total_count: 0, active_count: 0, inactive_count: 0 };
    this.sensorList$ = [];

    this.ngZone.runOutsideAngular(() => {
      const transportSensors$ = this.deviceService
        .getTransportInventory(this.customer_id)
        .pipe(untilDestroyed(this));

      transportSensors$
        .toPromise()
        .then((transportResponse) => {
          if (
            transportResponse?.data &&
            (transportResponse?.status === 200 ||
              !transportResponse.hasOwnProperty('status'))
          ) {
            const transportArray = Array.isArray(transportResponse.data)
              ? transportResponse.data
              : [transportResponse.data];
            this.sensorList$ = transportArray.map((sensor: any) => {
              return {
                ...sensor,
                sensor_type: 'transport',
                sensor_name: sensor.model_no || '-',
                display_name: sensor.parent_location_name || '-',
                last_seen_location_name:
                  sensor['last-seen-location_name'] || 'Unknown',
              };
            });
          } else {
            this.sensorList$ = [];
          }

          this.sensorList$.forEach((sensor) => {
            this.counts.total_count++;
            if (sensor?.is_active === true) this.counts.active_count++;
            if (sensor?.is_active === false) this.counts.inactive_count++;
          });

          this.getSearchDetails('');
          this.filteredSensorList$ = [...this.sensorList$];

          this.ngZone.run(() => {
            this.apiCallLoaders.isSensorsLoading = false;
          });
        })
        .catch((error) => {
          console.error('Error fetching sensors:', error);
          this.ngZone.run(() => {
            this.apiCallLoaders.isSensorsLoading = false;
          });
        });
    });
  }

  getWhitelistData() {
    this.deviceService
      .getWhitelistData()
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        if (response && response.result) {
          const whitelistData = response.result;
          this.modelNumbers = this.extractUniqueModelNumbers(whitelistData);
          this.deviceEUIs = whitelistData.filter(
            (item: any) => item.device_ui
          );
        }
      });
  }

  getLocationByCustomerId() {
    this.commonService
      .getLocationList(Number(this.customer_id))
      .subscribe({
        next: (response: any) => {
          const locations = Array.isArray(response?.data)
            ? response.data
            : [];
          this.locationOptions = locations;
        },
        error: (err) => {
          console.error('Error fetching locations', err);
          this.commonService.message.error('Failed to fetch locations');
        },
      });
  }

  refreshHandler(): void {
    this.sensorList$ = [];
    this.filteredSensorList$ = [];
    this.quickFilter = 'all';
    this.searchText = '';
    this.getSensorsbyCustomerId();
  }

  filterData(): void {
    let tempList = [...this.sensorList$];

    if (this.quickFilter === 'active') {
      tempList = tempList.filter((sensor) => sensor.is_active);
    } else if (this.quickFilter === 'inActive') {
      tempList = tempList.filter((sensor) => !sensor.is_active);
    }

    this.filteredSensorList$ = tempList;
  }

  getSearchDetails(val: any): void {
    let searchText = (val || '').trim().toLowerCase();
    this.filteredSensorList$ = this.sensorList$.filter((sensor) => {
      if (!searchText) return true;

      return (
        (sensor.sensor_name &&
          sensor.sensor_name.toLowerCase().includes(searchText)) ||
        (sensor.deviceeui &&
          sensor.deviceeui.toLowerCase().includes(searchText)) ||
        (sensor.display_name &&
          sensor.display_name.toLowerCase().includes(searchText)) ||
        (sensor.model_no &&
          sensor.model_no.toLowerCase().includes(searchText))
      );
    });
  }

  // Modal and form handlers
  showModal(type: string): void {
    if (type === 'excel') {
      this.isExcelModalVisible = true;
    } else if (type === 'sensor') {
      this.isSensorModalVisible = true;
    }
  }

  handleCancel(type: string): void {
    if (type === 'excel') {
      this.isExcelModalVisible = false;
      this.fileList = [];
      this.resetExcelState();
    } else if (type === 'sensor') {
      this.isSensorModalVisible = false;
      this.originalTransportData = null;
    }
  }

  addEditAsset(type: string, data?: any) {
    this.isEditMode = type === 'edit';
    this.editData = data;

    if (this.isEditMode && data) {
      if (data?.model_no) {
        const exists = this.modelNumbers.some(
          (m) => m.model_no === data.model_no
        );
        if (!exists) {
          this.modelNumbers = [
            ...this.modelNumbers,
            {
              model_no: data.model_no,
              capabilities: Array.isArray(data.capabilities)
                ? data.capabilities.join(', ')
                : data.capabilities,
            },
          ];
        }
      }

      this.originalTransportData = {
        serial_no: data?.serial_no,
        model_no: data?.model_no,
        deviceeui: data?.deviceeui,
        capabilities: Array.isArray(data?.capabilities)
          ? data?.capabilities
          : data?.capabilities
          ? data?.capabilities.split(',').map((c: string) => c.trim())
          : [],
        parent_location: data?.parent_location,
      };

      this.transportForm.patchValue({
        serial_no: data?.serial_no,
        model_no: data?.model_no,
        deviceeui: data?.deviceeui,
        capabilities: Array.isArray(data?.capabilities)
          ? data?.capabilities.join(', ')
          : data?.capabilities,
        parent_location: data?.parent_location,
      });

      this.showModal('sensor');
    } else {
      this.transportForm.reset();
      this.showModal('sensor');
    }
  }

  onSubmit() {
    const currentForm = this.transportForm;

    if (currentForm.invalid) {
      Object.keys(currentForm.controls).forEach((key) => {
        const control = currentForm.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const formData: any = {
      serial_no: currentForm.value.serial_no,
      model_no: currentForm.value.model_no,
      deviceeui: currentForm.value.deviceeui,
      capabilities: currentForm.value.capabilities
        ? typeof currentForm.value.capabilities === 'string'
          ? currentForm.value.capabilities
              .split(',')
              .map((c: string) => c.trim())
          : currentForm.value.capabilities
        : [],
      parent_location: currentForm.value.parent_location,
    };

    this.isConfirmLoading = true;

    let apiCall: any;

    if (this.isEditMode && this.editData && this.originalTransportData) {
      const changedFields = this.commonService.getChangedFields(
        this.originalTransportData,
        formData
      );

      const inventoryId = this.editData.id;
      apiCall = this.deviceService.updateTransportInventory(
        inventoryId,
        changedFields
      );
    } else {
      apiCall = this.deviceService.addTransportInventory(
        formData,
        this.customer_id
      );
    }

    apiCall.pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        if (response?.status === 200) {
          this.commonService.message.success(
            response?.data?.message ||
              (this.isEditMode
                ? 'Sensor updated successfully'
                : 'Sensor added successfully')
          );
          this.isSensorModalVisible = false;
          this.originalTransportData = null;
          this.getSensorsbyCustomerId();
        } else {
          this.commonService.message.error(
            response?.message ||
              (this.isEditMode
                ? 'Failed to update sensor'
                : 'Failed to add sensor')
          );
        }
        this.isConfirmLoading = false;
      },
      error: (error: any) => {
        this.commonService.message.error(
          this.isEditMode
            ? 'An error occurred while updating the sensor'
            : 'An error occurred while adding the sensor'
        );
        console.error('Error:', error);
        this.isConfirmLoading = false;
      },
    });
  }

  deleteTransportSensor(sensor: any) {
    this.commonService.modalSvc.confirm({
      nzTitle: 'Delete Transport Sensor',
      nzContent: `Are you sure you want to delete transport sensor with serial number "${sensor.serial_no}"? This action cannot be undone.`,
      nzOkText: 'Yes, Delete',
      nzCancelText: 'Cancel',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => {
        this.deviceService
          .deleteTransportInventory(sensor.id)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: () => {
              this.commonService.message.success(
                'Transport sensor deleted successfully'
              );
              this.getSensorsbyCustomerId();
            },
            error: (error: any) => {
              console.error('Error deleting transport sensor:', error);
            },
          });
      },
    });
  }

  onFileChange(event: any): void {
    const file = event.target.files;
    const newFiles = Array.from(file);
    for (const f of newFiles as File[]) {
      const fileName = f.name.toLowerCase();
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const isValidType = validExtensions.some((ext) => fileName.endsWith(ext));
      if (!isValidType) {
        this.commonService.message.error(
          `File ${f.name} is not an allowed file type. Please upload excel file only.`
        );
        continue;
      }
      this.fileList.push(f);
    }
  }

  removeFile(index: number): void {
    this.fileList.splice(index, 1);
  }

  validateExcelFile(): boolean {
    if (this.fileList.length === 0) {
      this.commonService.message.error('Please select a file to upload');
      return false;
    }
    return true;
  }

  prepareExcelFormData(): FormData {
    const formData = new FormData();
    formData.append('file', this.fileList[0], this.fileList[0].name);
    return formData;
  }

  downloadTemplate() {
    const headers = this.TRANSPORT_EXPECTED_HEADERS;
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers]);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const sheetName = 'Transport Sensor list';
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const fileName = 'Add Transport Sensor Template.xlsx';
    XLSX.writeFile(wb, fileName);
  }

  onExcelSubmit() {
    if (!this.validateExcelFile()) {
      return;
    }
    this.resetExcelState();
    this.isConfirmLoading = true;

    const formData = this.prepareExcelFormData();

    const apiCall = this.deviceService.addTransportInventoryExcel(
      formData
    );

    apiCall.pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        this.handleExcelUploadResponse(response);
        this.isConfirmLoading = false;
      },
      error: (error: any) => {
        this.handleExcelUploadError(error);
        this.isConfirmLoading = false;
      },
    });
  }

  resetExcelState(): void {
    this.excelErr = '';
    this.excelErrLink = '';
    this.isSuccessMessage = false;
  }

  handleExcelUploadResponse(response: any): void {
    if (response?.status === 200) {
      if (response?.result) {
        this.excelErrLink = response.result;
      }
      this.isSuccessMessage = true;
      this.excelErr = 'Excel file processed successfully';

      if (!this.excelErrLink) {
        this.isExcelModalVisible = false;
        this.fileList = [];
      }
      this.getSensorsbyCustomerId();
    } else {
      this.isSuccessMessage = false;
      this.excelErr =
        response?.message ||
        response?.error_message ||
        'Failed to process excel file';
      if (response?.log_file_url) {
        this.excelErrLink = response.log_file_url;
      }
      this.commonService.message.error(this.excelErr);
    }
  }

  handleExcelUploadError(error: any): void {
    this.isSuccessMessage = false;
    this.excelErr =
      error?.error?.message ||
      'An error occurred while uploading the excel file';
    if (error?.error?.log_file_url) {
      this.excelErrLink = error.error.log_file_url;
    }
    this.commonService.message.error(this.excelErr);
    console.error('Error uploading excel:', error);
  }

  downloadLogFile() {
    if (this.excelErrLink) {
      window.open(this.excelErrLink, '_blank');
    }
  }

  extractUniqueModelNumbers(data: any[]): any[] {
    const modelMap = new Map();

    data.forEach((item) => {
      if (item.model_no && !modelMap.has(item.model_no)) {
        modelMap.set(item.model_no, {
          model_no: item.model_no,
          capabilities: item.reading_type,
        });
      }
    });

    return Array.from(modelMap.values());
  }

  onModelNoChange(modelNo: string) {
    const selectedModel = this.modelNumbers.find(
      (model) => model.model_no === modelNo
    );
    if (selectedModel) {
      this.transportForm.patchValue({
        capabilities: selectedModel.capabilities,
        deviceeui: null,
      });
      this.filteredDeviceEUIs = this.deviceEUIs.filter(
        (device) => device.model_no === modelNo
      );
    }
  }

  onDeviceEUISearch(event: any) {
    const searchTerm = event.target.value.toLowerCase();
    const modelFiltered = this.deviceEUIs.filter(
      (device) =>
        device.model_no === this.transportForm.get('model_no')?.value
    );

    if (searchTerm) {
      this.filteredDeviceEUIs = modelFiltered.filter(
        (device) =>
          device.device_ui &&
          device.device_ui.toLowerCase().includes(searchTerm)
      );
    } else {
      this.filteredDeviceEUIs = modelFiltered;
    }
  }

  selectDeviceEUI(device: any) {
    this.transportForm.patchValue({
      deviceeui: device.device_ui,
    });

    const selectedDeviceData = this.deviceEUIs.find(
      (item) =>
        item.device_ui === device.device_ui &&
        item.model_no === this.transportForm.get('model_no')?.value
    );

    if (selectedDeviceData) {
      const serialNoValue = selectedDeviceData.serial_no || '';
      const capabilitiesValue =
        selectedDeviceData.reading_type != null
          ? Array.isArray(selectedDeviceData.reading_type)
            ? selectedDeviceData.reading_type.join(', ')
            : selectedDeviceData.reading_type
          : null;

      this.transportForm.patchValue({ serial_no: serialNoValue });
      if (capabilitiesValue != null) {
        this.transportForm.patchValue({ capabilities: capabilitiesValue });
      }
    } else {
      this.transportForm.patchValue({ serial_no: '' });
    }

    this.filteredDeviceEUIs = [];
  }

  getTransportSensorDetails(sensor: any): string {
    if (!sensor?.last_reading) return 'No data';
    const reading = sensor.last_reading;
    const details = [];

    if (reading.Temperature !== undefined) {
      details.push(`Temp: ${reading.Temperature}°C`);
    }
    if (reading.Humidity !== undefined) {
      details.push(`Humidity: ${reading.Humidity}%`);
    }
    if (reading.Battery !== undefined) {
      details.push(`Battery: ${reading.Battery}%`);
    }

    return details.join(' | ') || 'No data';
  }

  formatDateToIST(dateString: string): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) return '-';

      const options: Intl.DateTimeFormatOptions = {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      };

      return new Intl.DateTimeFormat('en-IN', options).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  }
}

